/**
 * Pure evening-rollover math behind the "Tonight at the studio" home widget.
 *
 * Bobbie's ask, in her words: "it should show tonight's classes, and then when
 * a class has ended the next one is on top and the past one removes from the
 * list ... Also the last 15 mins of the class a banner reminder should pop up
 * to ask if they have new music or a new video to upload."
 *
 * All of that is a function of one list plus one instant. Keeping it here,
 * away from React and away from the Firebase SDK, means it can be run
 * directly by `node --experimental-strip-types` in tests/tonight.test.mjs.
 * The widget is left with rendering and a one-minute timer, which is the part
 * that cannot be unit tested anyway.
 *
 * NOTHING in this file may import a local module without a file extension:
 * Node's type stripping does not run the bundler's resolver, so an
 * extensionless import here would break the test run. It imports nothing.
 */

/** How close to the end of a class the upload nudge starts asking. */
export const FINAL_STRETCH_MINUTES = 15;

const MINUTE_MS = 60_000;

/**
 * A reservation reduced to the four fields this math needs, with the
 * timestamps already resolved to epoch milliseconds.
 */
export interface TonightSlot {
  id: string;
  startMs: number;
  endMs: number;
  /** Null for a room block or a studio event that is not a class. */
  offeringId: string | null;
}

/** The raw shape coming off a Firestore reservation doc. */
export interface TonightSource {
  id: string;
  startAt: unknown;
  endAt: unknown;
  offeringId?: string | null;
}

export interface TonightPlan {
  /**
   * Everything that has not finished yet, soonest first. A class that has
   * ended is gone from this list, which is the whole roll-off behavior: the
   * next class simply becomes index 0.
   */
  upcoming: TonightSlot[];
  /** Happening right now, i.e. started and not yet ended. Usually one row. */
  current: TonightSlot[];
  /** Already finished. Kept so a caller can say "3 done" rather than hide it. */
  ended: TonightSlot[];
  /**
   * EVERY class inside its final FINAL_STRETCH_MINUTES, soonest ending first.
   * Only ever slots with an offeringId, because the nudge asks about music and
   * video for a class and a blocked room has neither.
   *
   * Plural because the studio runs five rooms at once. Picking a single winner
   * here would mean at most one teacher on the floor could ever be nudged, and
   * on the printed grid classes routinely share an end time, so the winner
   * would come down to whichever Firestore auto-id sorted first. Who is asked
   * depends on who is looking, and only the caller knows that: hand it the
   * candidates and let it call pickFinalStretchFor.
   */
  finalStretchCandidates: TonightSlot[];
}

/**
 * Epoch millis from a Firestore Timestamp, a Date, or a plain number, and
 * null from anything else.
 *
 * Demo docs and hand-seeded rows really do carry missing and half-formed
 * timestamps, and an unguarded .toMillis() on one of those has taken this
 * widget's neighbours down before. Every read of a reservation time in the
 * widget goes through here.
 */
export function tsToMillis(value: unknown): number | null {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const ts = value as { toMillis?: () => number; toDate?: () => Date } | null | undefined;
  if (!ts || typeof ts !== 'object') return null;
  try {
    if (typeof ts.toMillis === 'function') {
      const ms = ts.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof ts.toDate === 'function') {
      const ms = ts.toDate().getTime();
      return Number.isFinite(ms) ? ms : null;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Normalize reservation docs into slots, dropping any row whose times cannot
 * be read or whose end is not after its start. A malformed booking is skipped
 * rather than allowed to sit at the top of the list forever with a NaN time.
 */
export function toTonightSlots(items: readonly TonightSource[]): TonightSlot[] {
  const out: TonightSlot[] = [];
  for (const item of items) {
    const startMs = tsToMillis(item.startAt);
    const endMs = tsToMillis(item.endAt);
    if (startMs === null || endMs === null) continue;
    if (endMs <= startMs) continue;
    out.push({
      id: item.id,
      startMs,
      endMs,
      offeringId: item.offeringId ?? null,
    });
  }
  return out;
}

/**
 * Split the evening around a given instant.
 *
 * Boundaries, chosen deliberately and pinned by tests:
 *   - A class is OVER the moment the clock reaches its end time. endMs === now
 *     drops off. Nobody wants to tap into attendance for a class that is
 *     already emptying out.
 *   - A class is NOW from its start time up to but not including its end.
 *   - The nudge window is the last FINAL_STRETCH_MINUTES of a class that is
 *     currently running: 15 minutes remaining fires, 16 does not, and one
 *     second after the end does not, because by then it is not running.
 */
export function planTonight(
  slots: readonly TonightSlot[],
  nowMs: number
): TonightPlan {
  const upcoming: TonightSlot[] = [];
  const current: TonightSlot[] = [];
  const ended: TonightSlot[] = [];

  for (const slot of slots) {
    if (slot.endMs <= nowMs) {
      ended.push(slot);
      continue;
    }
    upcoming.push(slot);
    if (slot.startMs <= nowMs) current.push(slot);
  }

  upcoming.sort(compareSlots);
  current.sort((a, b) => a.endMs - b.endMs || compareSlots(a, b));
  ended.sort(compareSlots);

  const windowMs = FINAL_STRETCH_MINUTES * MINUTE_MS;
  // current is already sorted by end time, so the candidates come out in the
  // order a caller wants to consider them: the class about to walk out first.
  const finalStretchCandidates = current.filter(
    (slot) => slot.offeringId !== null && slot.endMs - nowMs <= windowMs
  );

  return { upcoming, current, ended, finalStretchCandidates };
}

/**
 * The one class to nudge THIS person about, or null.
 *
 * Split out from planTonight because the viewer is not a property of the
 * evening. Two teachers looking at the same 5:50pm both need their own answer,
 * and a teacher with nothing running needs none. Pure, so the multi-room case
 * is pinned by a test rather than by whoever happens to open the home screen.
 *
 * When somebody is somehow down for two overlapping classes, the one ending
 * soonest wins: it is the one whose dancers are already putting shoes on.
 */
export function pickFinalStretchFor(
  candidates: readonly TonightSlot[],
  myOfferingIds: ReadonlySet<string>
): TonightSlot | null {
  for (const slot of candidates) {
    if (slot.offeringId !== null && myOfferingIds.has(slot.offeringId)) return slot;
  }
  return null;
}

function compareSlots(a: TonightSlot, b: TonightSlot): number {
  return a.startMs - b.startMs || a.endMs - b.endMs || a.id.localeCompare(b.id);
}

/** True when this slot is the one happening right now. */
export function isHappeningNow(slot: TonightSlot, nowMs: number): boolean {
  return slot.startMs <= nowMs && nowMs < slot.endMs;
}

/**
 * Whether a Tonight row should print its start-time label.
 *
 * Bobbie, packet J4, verbatim: "as we approach the 5pm hour can the 5pm go
 * away." Once a class wears the NOW pill its clock time is not telling
 * anybody anything the pill does not already say, so the row reads class,
 * NOW, room instead of class, 5:00, NOW, room. A class that has not started
 * yet keeps its time (it is the only way to tell two upcoming rows apart),
 * and a finished class never reaches this: planTonight already dropped it
 * from `upcoming` by the time a row would ask.
 */
export function tonightShowTimeLabel(slot: TonightSlot, nowMs: number): boolean {
  return !isHappeningNow(slot, nowMs);
}

/** Whole minutes left in a slot, floored at zero. */
export function minutesLeft(slot: TonightSlot, nowMs: number): number {
  return Math.max(0, Math.ceil((slot.endMs - nowMs) / MINUTE_MS));
}

/**
 * YYYY-MM-DD in the DEVICE's local time.
 *
 * NOT an attendance day key. An attendance mark is
 * {offeringId}_{studio day}_{studentId}, and /attendance, the nag and every
 * door into them read America/Chicago through lib/studioDay.ts's
 * studioDayKey. This used to say it "matches the date format /attendance
 * expects", which was read as permission to build attendance dates with it,
 * and on any device set to UTC a 7:30pm Central class was filed under
 * tomorrow (lane G, finding G1). Use it for a device-local display day and
 * for round-tripping a date this app built locally; never for a day key
 * anybody else has to agree with.
 */
export function dayKey(value: Date | number): string {
  const d = value instanceof Date ? value : new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Attendance mark counts for one class on one day, enough to render the
 * "Take attendance" button's confirmed state on a Tonight row.
 *
 * Bobbie's ask: she took attendance for a class, went back to the home
 * screen, and the button still read plain "Take attendance" as if she had
 * not - it should say attendance was taken and still let her tap through
 * to edit.
 *
 * Mirrors AttendanceStatus in lib/attendance.ts as plain fields rather than
 * importing it, per this file's "imports nothing" rule at the top, so this
 * keeps running under node --experimental-strip-types in tests/tonight.test.mjs.
 */
export interface TonightAttendanceCounts {
  present: number;
  late: number;
  absent: number;
}

/** One attendance row as this widget reads it. Deliberately not importing AttendanceDoc: see the header. */
export interface TonightAttendanceMark {
  studentId: string;
  status: string;
}

/**
 * Tally today's rows against the class's CURRENT roster.
 *
 * Two things it refuses to do, both of which would let the button overstate
 * how done attendance is. A row for somebody no longer on this roster (a
 * dancer moved out of the class mid-term, a stray from an import) cannot fill
 * a roster slot, and neither can a second row for a dancer already counted.
 * Anything whose status is not one of the four recognized marks is not a mark
 * at all and is ignored rather than tallied under a made-up heading.
 */
export function tonightAttendanceCountsForRoster(
  marks: readonly TonightAttendanceMark[],
  rosterStudentIds: readonly string[]
): TonightAttendanceCounts {
  const roster = new Set(rosterStudentIds);
  const counted = new Set<string>();
  const counts: TonightAttendanceCounts = { present: 0, late: 0, absent: 0 };
  for (const mark of marks) {
    if (!roster.has(mark.studentId) || counted.has(mark.studentId)) continue;
    // 'excused' left dancer attendance on 2026-08-23; a pre-migration mark
    // from a stale cache folds into absent, same as every other reader.
    const status = mark.status === 'excused' ? 'absent' : mark.status;
    if (status !== 'present' && status !== 'late' && status !== 'absent') {
      continue;
    }
    counted.add(mark.studentId);
    counts[status] += 1;
  }
  return counts;
}

/** How much of one class's roster is marked, and whether that is all of it. */
export interface TonightAttendanceCoverage {
  /** Roster dancers with a recognized mark today. */
  marked: number;
  /** Roster dancers still waiting for one. */
  unmarked: number;
  /** True only once every dancer on TODAY's roster has a mark. */
  taken: boolean;
}

/**
 * THE ONE ANSWER TO "IS ATTENDANCE TAKEN", and there is exactly one on
 * purpose.
 *
 * There used to be two, and they disagreed on the same row at the same
 * moment. The button said "Attendance started, 5 unmarked" while the
 * checkmark beside it said done, because the checkmark deliberately copied
 * the nag's question, which was "does any mark exist" - one dancer tapped
 * closed the register, the to-do filed itself as finished, and the five
 * dancers nobody ever marked were invisible to the report, to the studio
 * rate and to the concerns list for good (lane G, finding G4).
 *
 * `rosterCount` of zero is the one case that answers taken from marks alone:
 * a class with marks has dancers, so a roster this caller could not count is
 * reported rather than turned into an invented unmarked number. An empty
 * roster with no marks is not taken, and never nags - attendanceNag.mjs
 * retires that class through offeringRosterIsEmpty instead.
 */
export function tonightAttendanceCoverage(
  counts: TonightAttendanceCounts | null,
  rosterCount: number
): TonightAttendanceCoverage {
  if (!counts) return { marked: 0, unmarked: Math.max(0, rosterCount), taken: false };
  const marked = counts.present + counts.late + counts.absent;
  const unmarked = Math.max(0, rosterCount - marked);
  return { marked, unmarked, taken: marked > 0 && unmarked === 0 };
}

/**
 * The same answer from the RAW marks, for a caller holding rows rather than a
 * tally: the Tonight row's checkmark and netlify/functions/attendanceNag.mjs,
 * which imports this file so the screen and the to-do cannot drift apart.
 *
 * Off-roster and duplicate rows cannot fill a roster slot
 * (tonightAttendanceCountsForRoster), so a stray mark left behind by a dancer
 * who moved out of the class can never close a register for the dancers who
 * are still in it.
 */
export function attendanceTakenForRoster(
  marks: readonly TonightAttendanceMark[] | null,
  rosterStudentIds: readonly string[]
): boolean {
  if (!marks) return false;
  return tonightAttendanceCoverage(
    tonightAttendanceCountsForRoster(marks, rosterStudentIds),
    rosterStudentIds.length
  ).taken;
}

export interface TonightAttendanceButtonState {
  /** True only once every dancer on the roster has a mark. Anything less is started, not taken. */
  taken: boolean;
  /** The exact button copy - see the doc below for the three shapes this takes. */
  label: string;
}

/**
 * The Tonight row's "Take attendance" button copy and done/not-done state.
 *
 * THREE states, not two, and the middle one is the point. A single mark used
 * to flip this to "Attendance taken", so a teacher who tapped one dancer and
 * got pulled away came back to a home screen telling her the register was
 * done. Twelve unmarked dancers is not a taken register: it is a started one,
 * and it says so, with the number still outstanding.
 *
 * `counts` is null while the caller has not yet asked (still loading, or this
 * offering was not among the ones queried) - treated the same as "no marks
 * yet" on purpose, so the button never GUESSES it is done; it only upgrades
 * once real data confirms it. Same reasoning for an all-zero tally, which
 * cannot actually happen once a real mark exists but is handled the safe way
 * regardless.
 *
 * `rosterCount` is how many dancers are on the class today. A roster of zero
 * with marks on it should not happen, and if it ever does the label reports
 * the marks rather than claiming a completeness it cannot check.
 *
 * The tally is included whenever marks exist rather than gated behind a
 * separate flag: it comes from the exact same query as the taken/not-taken
 * answer, so showing it costs nothing extra.
 */
export function tonightAttendanceButtonState(
  counts: TonightAttendanceCounts | null,
  rosterCount: number
): TonightAttendanceButtonState {
  if (!counts) return { taken: false, label: 'Take attendance' };
  const parts: string[] = [];
  if (counts.present > 0) parts.push(`${counts.present} here`);
  if (counts.late > 0) parts.push(`${counts.late} late`);
  if (counts.absent > 0) parts.push(`${counts.absent} absent`);
  // ONE definition of taken, shared with the checkmark on this same row and
  // with the nag. See tonightAttendanceCoverage.
  const { marked, unmarked, taken } = tonightAttendanceCoverage(counts, rosterCount);
  if (marked === 0) return { taken: false, label: 'Take attendance' };
  if (!taken) {
    return {
      taken: false,
      label: `Attendance started - ${parts.join(', ')} - ${unmarked} unmarked`,
    };
  }
  return { taken: true, label: `Attendance taken - ${parts.join(', ')} - tap to edit` };
}

/**
 * THE SAME FACT, SAID TO SOMEBODY WHO CANNOT ACT ON IT.
 *
 * tonightAttendanceButtonState above writes a BUTTON label, and every one of
 * its strings is an instruction: "Take attendance", "tap to edit". Round six
 * gave the front desk and a Jr Staff on her own dancing class the state
 * without the link, and rendering the button label into that inert span told
 * them to tap something that does nothing - the exact "control the rules will
 * refuse" trap in docs/lessons.md, wearing a different hat.
 *
 * So a reader who cannot open the register gets the FACT and no instruction:
 * whether the register is done, and the tally, in words that ask for nothing.
 * '' for a class nobody has marked yet, which the caller renders as no line
 * at all - "not taken yet" at 6pm for a class that starts at 7 is noise on a
 * front desk reading twenty rows.
 *
 * Pure, same inputs as the button state, so the two can never disagree about
 * whether attendance is taken.
 */
export function tonightAttendanceFactLabel(
  counts: TonightAttendanceCounts | null,
  rosterCount: number
): string {
  if (!counts) return '';
  const parts: string[] = [];
  if (counts.present > 0) parts.push(`${counts.present} here`);
  if (counts.late > 0) parts.push(`${counts.late} late`);
  if (counts.absent > 0) parts.push(`${counts.absent} absent`);
  const { marked, unmarked, taken } = tonightAttendanceCoverage(counts, rosterCount);
  if (marked === 0) return '';
  if (!taken) return `Attendance started, ${parts.join(', ')}, ${unmarked} unmarked`;
  return `Attendance taken, ${parts.join(', ')}`;
}

/**
 * localStorage key for a dismissed upload nudge.
 *
 * Keyed by the BOOKING, not by the class. The widget re-evaluates every
 * minute, so without a sticky key the banner a teacher just waved away would
 * be back sixty seconds later for the rest of the class. But a comp-class
 * holds its weekly class and its rehearsal on the same day, and keying by
 * class plus day meant waving the banner off after the 4pm class also killed
 * the 7pm one, silently, for the session that actually had new music. The day
 * stays in the key so it reads like a date in devtools; the reservation id is
 * what makes it specific.
 */
export function nudgeDismissKey(reservationId: string, day: string): string {
  return `ldg.tonight.nudge.${day}.${reservationId}`;
}

/**
 * The SAME "attendance taken" signal `netlify/functions/attendanceNag.mjs`
 * uses to close its nag task - and now that really is the same signal, which
 * it was not before.
 *
 * It used to be "at least one attendance record exists", matching what the
 * nag asked, and the header here said the two could then never disagree.
 * They disagreed on the same row: the BUTTON beside this checkmark answers
 * the roster-complete question, so one class could read "Attendance started,
 * 5 unmarked" with a done tick next to it. Both sides now ask
 * tonightAttendanceCoverage, and so does the nag (lane G, finding G4).
 *
 * `offeringId` null (a room block, not a class) and `marks` null (no data
 * yet - the listener has not reported back, or this offering was not among
 * the ones queried) both read as not-done, on purpose: a checkmark this
 * function is not sure about is worse than a checkmark that is a beat late.
 */
export function tonightAttendanceDone(
  offeringId: string | null,
  marks: readonly TonightAttendanceMark[] | null,
  rosterStudentIds: readonly string[]
): boolean {
  if (offeringId === null || marks === null) return false;
  return attendanceTakenForRoster(marks, rosterStudentIds);
}

/** One pill in a Tonight row's teacher/assistant group. */
export interface TonightPill {
  label: string;
  kind: 'teacher' | 'assistant';
}

/**
 * The teacher and Jr assistant pills for a Tonight row.
 *
 * Bobbie, packet J4, verbatim: "Can we add the teachers on this view also.
 * As a pill"; "...Jr assistants as a second pill when present."
 *
 * Takes already-resolved display strings, never uids or user docs: this
 * file imports nothing (see the header), so the identity collapse that
 * keeps a linked Jr Staff pair down to one name - oneEntryPerHuman,
 * lib/jrStaffRoster.ts - has to run in the caller, which can read the
 * staff directory. This function's only job is turning that finished name
 * list, plus the class's already-joined Jr assistant label
 * (offeringStaffAssistantLabel's "Jr: Name, Name"), into the row's pill
 * group: one pill per teacher name, in order, then one assistant pill last
 * when the class has any Jr assistants.
 */
export function tonightTeacherPills(
  teacherNames: readonly string[],
  assistantLabel: string | null
): TonightPill[] {
  const pills: TonightPill[] = teacherNames
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .map((label) => ({ label, kind: 'teacher' as const }));
  const trimmedAssistantLabel = assistantLabel?.trim();
  if (trimmedAssistantLabel) {
    pills.push({ label: trimmedAssistantLabel, kind: 'assistant' as const });
  }
  return pills;
}

/**
 * Every distinct uid across tonight's classes, first-seen order kept.
 *
 * Quota-incident follow-up (2026-09-10, TASK-QUEUE.md "FIRESTORE READ
 * BUDGET", docs/lessons.md "Firestore quota on a class night"): the Tonight
 * widget used to open useUsersList() to resolve teacher/Jr assistant names,
 * which for a Director is a BARE, LIVE listener on the entire users
 * collection, on the screen Directors open most. This function is the pure
 * half of the bounded replacement: it takes the ALREADY-RESOLVED per-offering
 * uid lists (offeringTeacherUids + offeringAssistantUids, lib/offeringTeachers.ts
 * - never an offering doc, since this file imports nothing, see the header)
 * and flattens them to the one list the caller fetches, ONCE, by id -
 * useUsersByIds (lib/users.ts), one getDoc per uid. (An earlier cut of this
 * file also carried a chunkUids for a chunked `documentId() 'in'` query;
 * dropped when useUsersByIds moved to per-uid getDoc calls - see that
 * hook's header for why the 'in' query does not prove for every role.)
 */
export function tonightDirectoryUids(
  perOfferingUidLists: readonly (readonly string[])[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const uids of perOfferingUidLists) {
    for (const uid of uids) {
      if (!uid || seen.has(uid)) continue;
      seen.add(uid);
      out.push(uid);
    }
  }
  return out;
}

/** One collapsed staff identity on a Tonight row, teacher or assistant. */
export interface TonightStaffUid {
  /** The identity's canonical uid - an alias group's account uid when it has one, else the raw uid. Look a live name up under THIS uid. */
  uid: string;
  kind: 'teacher' | 'assistant';
  /**
   * Every raw uid (from teacherUids or assistantUids) that collapsed into
   * this one identity, in encounter order. A class's denormalized name
   * snapshot (offering.teacherNames / offering.assistantNames) is keyed by
   * the RAW uid a name was saved under, which is not always the alias
   * group's own canonical uid, so the caller tries these in order for its
   * fallback when the live directory has no entry for `uid`.
   */
  rawUids: string[];
}

/**
 * Combines a class's teacher and Jr assistant uids into ONE deduplicated,
 * ordered list, one entry per human - TEACHER WINS when the same person
 * appears in both groups under two linked accounts (PR #1086 review,
 * StaffHomeContent.tsx's tonightPillsForOffering: collapsing the two
 * uid-to-name lists SEPARATELY let a Jr Staff account teaching under one
 * uid and assisting under her other, linked uid survive as BOTH a teacher
 * pill and a `Jr:` pill - the same human shown twice under two different
 * labels).
 *
 * `aliasGroups` says which uids are the SAME human: each inner array is
 * one person's linked uids, canonical account uid FIRST (the shape
 * jrStaffAliasGroups already produces, lib/jrStaffAliases.ts - resolved by
 * the caller, since that needs the user directory and this file imports
 * nothing). A uid absent from every group is its own identity of one.
 * Teacher uids are walked first, so when a group has no account uid of its
 * own (a plain single-uid group, or no group at all) the FIRST list this
 * person appears on decides where the pill lands, teacher over assistant.
 */
export function tonightCombinedStaffUids(
  teacherUids: readonly string[],
  assistantUids: readonly string[],
  aliasGroups: readonly (readonly string[])[]
): TonightStaffUid[] {
  const canonicalOf = new Map<string, string>();
  for (const group of aliasGroups) {
    const account = group[0];
    if (!account) continue;
    for (const uid of group) canonicalOf.set(uid, account);
  }
  const canonical = (uid: string): string => canonicalOf.get(uid) ?? uid;
  const teacherCanonical = new Set(teacherUids.filter(Boolean).map(canonical));

  const byCanonical = new Map<string, TonightStaffUid>();
  const order: string[] = [];
  for (const uid of [...teacherUids, ...assistantUids]) {
    if (!uid) continue;
    const c = canonical(uid);
    let entry = byCanonical.get(c);
    if (!entry) {
      entry = { uid: c, kind: teacherCanonical.has(c) ? 'teacher' : 'assistant', rawUids: [] };
      byCanonical.set(c, entry);
      order.push(c);
    }
    if (!entry.rawUids.includes(uid)) entry.rawUids.push(uid);
  }
  return order.map((c) => byCanonical.get(c) as TonightStaffUid);
}

/**
 * Which offering ids the attendance-done listener (useTonightAttendanceMarks,
 * lib/attendance.ts) should cover: the first `visibleCount` slots while the
 * Tonight list is collapsed, every slot in `slots` once it is expanded.
 *
 * PR #1086 review, P2: rows revealed by "+N more" never got the attendance
 * checkmark, because the listener's offering-id set stayed capped at
 * TONIGHT_VISIBLE even after the in-place expand showed every class left
 * tonight - the how-to guide entry promised the checkmark on any visible
 * row, and this closed the gap between "visible" and "queried" instead of
 * rewriting the promise. Distinct offering ids, first-seen order kept, a
 * room block's null offeringId dropped (it has no attendance to check).
 */
export function tonightAttendanceQueryOfferingIds(
  slots: readonly TonightSlot[],
  expanded: boolean,
  visibleCount: number
): string[] {
  const source = expanded ? slots : slots.slice(0, visibleCount);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const slot of source) {
    if (!slot.offeringId || seen.has(slot.offeringId)) continue;
    seen.add(slot.offeringId);
    out.push(slot.offeringId);
  }
  return out;
}
